// src/pages/Dashboard.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { hamtaBestallarnamn, arBrfKund, hamtaSokbartKundnamn } from '../utils/nameUtils';
import AddProject from '../components/Projects/AddProject';
import UpdateProject from '../components/Projects/UpdateProject';
import RemoveProject from '../components/Projects/RemoveProject';
import { AddUserForm as AddUser, UpdateUserForm as UpdateUser, RemoveUser } from '../features/users';
import TestkundPanel from '../features/users/components/TestkundPanel';
import ProjektorContactAdmin from '../features/projektorer/ProjektorContactAdmin';
import { AddSupplierForm as AddSupplier, UpdateSupplierForm as UpdateSupplier, RemoveSupplier } from '../features/suppliers';
import { AddBuilderForm as AddBuilder, UpdateBuilderForm as UpdateBuilder, RemoveBuilder } from '../features/builders';
import Modal from '../components/Modal';
import ResultModal from '../components/common/ResultModal';
import Navbar from '../components/common/Navbar';
import { API_URL, getAvatarUrl, authHeaders } from '../utils/api';
import Avatar from '../components/common/Avatar';
import useApiCache from '../hooks/useApiCache';
import { useAuth } from '../contexts/AuthContext';
import { getProjectStatus as beraknaProjektStatus } from '../features/workspace/utils/projectStatus';
import PrislistaHantering from '../features/ia-prislista/components/PrislistaHantering';

// 🚀 OPTIMERING: Definiera fetch-funktioner utanför komponenten för stabilitet
const fetchProjects = async () => {
  // Routen kräver admin — utan token svarar den 401
  const response = await fetch(`${API_URL}/api/projects`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });
  if (!response.ok) throw new Error('Kunde inte hämta projekt');
  return response.json();
};

const fetchUsers = async () => {
  const response = await fetch(`${API_URL}/users?ts=${Date.now()}`, {
    headers: {
      ...authHeaders(),
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    },
    cache: 'no-store'
  });
  if (!response.ok) throw new Error('Kunde inte hämta användare');
  return response.json();
};

const fetchBuilders = async () => {
  const response = await fetch(`${API_URL}/builders`, { headers: authHeaders() });
  if (!response.ok) throw new Error('Kunde inte hämta byggare');
  return response.json();
};

const fetchSuppliers = async () => {
  const response = await fetch(`${API_URL}/suppliers`, { headers: authHeaders() });
  if (!response.ok) throw new Error('Kunde inte hämta leverantörer');
  return response.json();
};

function Dashboard() {
  const { user } = useAuth();

  const [tabIndex, setTabIndex] = useState(0);
  
  // 🚀 OPTIMERING: Ersätt individuella states med cached API-anrop
  const { data: projects = [],  refresh: refreshProjects } = useApiCache(
    'dashboard_projects',
    fetchProjects,
    300000 // 5 minuter cache
  );

  const { data: users = [], loading: usersLoading, refresh: refreshUsers } = useApiCache(
    'dashboard_users',
    fetchUsers,
    60000 // 1 minut cache för snabbare spegling
  );

  const { data: builders = [],  refresh: refreshBuilders } = useApiCache(
    'dashboard_builders',
    fetchBuilders,
    300000 // 5 minuter cache
  );

  const { data: suppliers = [],  refresh: refreshSuppliers } = useApiCache(
    'dashboard_suppliers',
    fetchSuppliers,
    300000 // 5 minuter cache
  );

  // 🚀 OPTIMERING: Filter states och loading state
  const [filterName, setFilterName] = useState('');
  const [userFilterName, setUserFilterName] = useState('');
  const [userType, setUserType] = useState('all');
  const [userAccountStatus, setUserAccountStatus] = useState('all'); // 'all' | 'active' | 'inactive'
  const [supplierFilterName, setSupplierFilterName] = useState('');
  const [cleaningDeclinedCustomers, setCleaningDeclinedCustomers] = useState(false);
  const [openAccountStatusDropdown, setOpenAccountStatusDropdown] = useState(null);
  const [openUserStatusDropdown, setOpenUserStatusDropdown] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  

  // States för snygga modals
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [modalResult, setModalResult] = useState(null);
  


  // Funktion för att få projektstatus.
  // Speglar logiken i features/workspace/sections/OversiktSection.js så att
  // System → Projekt visar samma rika status (Pågående · Försenat / Anmärkning)
  // som Workspace → Projekt. Manuell completionStatus tar precedens framför
  // datumbaserad härledning — projekt är "Avslutat" först när BK markerat det.
  // Delad beräkning — se features/workspace/utils/projectStatus.js
  const getProjectStatus = useCallback((project) => beraknaProjektStatus(project), []);

  // Stäng dropdowns när man klickar utanför
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openAccountStatusDropdown) {
        setOpenAccountStatusDropdown(null);
      }
      if (openUserStatusDropdown) {
        setOpenUserStatusDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openAccountStatusDropdown, openUserStatusDropdown]);

  // Lägg till högst upp bland states
  const [filterStatus, setFilterStatus] = useState({
    Planerad: true,
    Pågående: true,
    Avslutat: false,
    Saknas: true
  });

  // Lägg till ny state för team-sökfältet
  const [filterTeam, setFilterTeam] = useState('');

  // Funktion för att toggla statusfilter
  const handleStatusFilterChange = (status) => {
    setFilterStatus(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  // Funktion för att återställa filter
  const resetFilters = () => {
    setFilterName('');
    setFilterStatus({
      Planerad: true,
      Pågående: true,
      Avslutat: false,
      Saknas: true
    });
  };

  // Rensa declined kunder
  const [cleanPreview, setCleanPreview] = useState(null);

  // Steg 1: Hämta preview och visa i bekräftelsemodaln
  const handleCleanDeclinedCustomers = async () => {
    try {
      setCleaningDeclinedCustomers(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/admin/clean-declined-customers/preview`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Kunde inte hämta förhandsgranskning');
      const preview = await response.json();

      if (!preview.customers?.length) {
        setModalResult({ type: 'info', title: 'Inga att rensa', message: 'Inga declined kunder hittades.' });
        setShowResultModal(true);
        return;
      }

      setCleanPreview(preview);
      setShowConfirmationModal(true);
    } catch (error) {
      console.error('Fel vid preview:', error);
      setModalResult({ type: 'error', title: 'Fel', message: error.message });
      setShowResultModal(true);
    } finally {
      setCleaningDeclinedCustomers(false);
    }
  };

  // Steg 2: Bekräfta och kör rensning
  const cleanDeclinedCustomers = async () => {
    // Endpointen härleder inte längre vilka konton som ska bort — den kräver
    // en explicit lista. Kategorierna "Nej tack" och "inactive" betyder sedan
    // arkiveringsflödet "går att återställa", och en kategoriradering hade
    // tömt arkivet. Vi skickar därför exakt de konton preview visade.
    const customerIds = (cleanPreview?.customers || []).map(c => c._id);

    setShowConfirmationModal(false);
    setCleanPreview(null);

    if (customerIds.length === 0) {
      setModalResult({ type: 'info', title: 'Ingen rensning behövdes', message: 'Inga konton att rensa.' });
      setShowResultModal(true);
      return;
    }

    try {
      setCleaningDeclinedCustomers(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/users/admin/clean-declined-customers`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerIds }),
      });

      if (response.ok) {
        const result = await response.json();
        setModalResult({
          type: result.deletedUsers > 0 ? 'success' : 'info',
          title: result.deletedUsers > 0 ? 'Rensning slutförd!' : 'Ingen rensning behövdes',
          message: result.message,
        });
        setShowResultModal(true);
        if (result.deletedUsers > 0) await refreshUsers();
      } else {
        throw new Error('Fel vid rensning av declined kunder');
      }
    } catch (error) {
      console.error('Fel vid rensning:', error);
      setModalResult({ type: 'error', title: 'Rensning misslyckades', message: error.message });
      setShowResultModal(true);
    } finally {
      setCleaningDeclinedCustomers(false);
    }
  };









  // 🚀 OPTIMERING: User-builder mappning med memoization och projektstatus
  const processedUsers = useMemo(() => {
    if (!users?.length) return [];
    
    return users.map(user => {
      let builderInfo = null;
      if (builders?.length) {
        if (typeof user.builder === 'string') {
          builderInfo = builders.find(b => b._id === user.builder);
        } else if (user.builder?._id) {
          builderInfo = builders.find(b => b._id === user.builder._id);
        }
      }

      // Hitta användares projekt för att bestämma status (inkl. beställare 2)
      let userProjects = [];
      if (projects?.length && user.role === 'kund') {
        userProjects = projects.filter(project =>
          (project.customer && project.customer._id === user._id) ||
          (project.additionalCustomers?.some(c => c._id === user._id))
        );
      }

      // Bestäm användarens verkliga status
      let currentStatus = 'Ingen status';
      let hasActiveProject = false;
      
      if (userProjects.length > 0) {
        // Användaren har projekt - använd projektstatus eller "Projektering"
        hasActiveProject = true;
        
        // Kolla om något projekt har datum (timeline eller project-level)
        const projectWithDates = userProjects.find(project => {
          const hasTimelineDates = project.timeline?.startDate && project.timeline?.endDate;
          const hasProjectDates = project.startDate && project.endDate;
          return hasTimelineDates || hasProjectDates;
        });
        
        if (projectWithDates) {
          // Projekt med datum - använd riktig projektstatus
          const projectStatus = getProjectStatus(projectWithDates);
          currentStatus = projectStatus.status === 'Planerad start' ? 'Planerad' : projectStatus.status;
        } else {
          // Projekt utan datum - visa "Projektering"
          currentStatus = 'Projektering';
        }
      } else if (user.role === 'kund' && user.procurementStatus) {
        // Ingen projekt men har procurement status från backend
        currentStatus = user.procurementStatus;
      } else if (user.role === 'kund') {
        // Kund saknar både projekt och procurement status
        currentStatus = 'Ingen status';
      }

      return {
        ...user,
        builder: builderInfo,
        currentStatus,
        hasActiveProject,
        userProjects
      };
    });
  }, [users, builders, projects, getProjectStatus]);

  // 🚀 OPTIMERING: Refresh function som invaliderar alla caches
  const refreshAllData = useCallback(async () => {
    await Promise.all([
      refreshProjects(),
      refreshUsers(), 
      refreshBuilders(),
      refreshSuppliers()
    ]);
  }, [refreshProjects, refreshUsers, refreshBuilders, refreshSuppliers]);

  // Procurement-statusar (för kunder som inte har projekt än)
  const procurementStatuses = [
    { value: 'Förstudie', label: 'Förstudie', color: 'bg-blue-100 text-blue-800' },
    { value: 'Estimat', label: 'Estimat', color: 'bg-orange-100 text-orange-800' },
    { value: 'Hembesök', label: 'Hembesök', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'Kalkyl', label: 'Kalkyl', color: 'bg-cyan-100 text-cyan-800' },
    { value: 'PU', label: 'PU', color: 'bg-emerald-100 text-emerald-800' },
    { value: 'Offert', label: 'Offert', color: 'bg-purple-100 text-purple-800' },
    { value: 'Projekt', label: 'Projekt', color: 'bg-green-100 text-green-800' },
    { value: 'Nej tack', label: 'Nej tack', color: 'bg-red-100 text-red-800' }
  ];

  // Projekt-statusar (för kunder som har projekt)
  const projectStatuses = [
    { value: 'Projektering', label: 'Projektering', color: 'bg-violet-100 text-violet-800' },
    { value: 'Planerad', label: 'Planerad', color: 'bg-blue-100 text-blue-800' },
    { value: 'Pågående', label: 'Pågående', color: 'bg-leadme-orange-light text-leadme-orange-superdark' },
    { value: 'Avslutat', label: 'Avslutat', color: 'bg-green-100 text-green-800' }
  ];

  // Hantera statusändring för användare
  const handleStatusChange = async (user, newStatus) => {
    setUpdatingStatus(user._id);
    
    try {
      const token = localStorage.getItem('token');
      
      if (user.hasActiveProject && user.userProjects?.length > 0) {
        // Användaren har projekt - uppdatera projektstatus
        const project = user.userProjects[0]; // Ta första projektet
        
        const response = await fetch(`${API_URL}/api/projects/${project._id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus })
        });
        
        if (!response.ok) {
          throw new Error('Kunde inte uppdatera projektstatus');
        }
        
      } else {
        // Användaren är i procurement-fas - uppdatera procurement status
        const procResponse = await fetch(`${API_URL}/api/procurement/customer/${user._id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!procResponse.ok) {
          const errorText = await procResponse.text();
          console.error('❌ Kunde inte hämta procurements:', errorText);
          throw new Error('Kunde inte hämta upphandlingar för kunden');
        }
        
        const procurements = await procResponse.json();

        if (procurements.length === 0) {
          throw new Error('Kunden har ingen aktiv upphandling');
        }
        
        const procurement = procurements[0]; // Ta första/senaste

        const updateResponse = await fetch(`${API_URL}/api/procurement/${procurement._id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus })
        });
        
        if (!updateResponse.ok) {
          const errorData = await updateResponse.json().catch(() => ({}));
          console.error('❌ Kunde inte uppdatera:', errorData);
          throw new Error(errorData.message || 'Kunde inte uppdatera upphandlingsstatus');
        }
        
      }

      // Uppdatera datan
      await refreshAllData();
      
    } catch (error) {
      console.error('Fel vid statusuppdatering:', error);
      alert('Kunde inte uppdatera status: ' + error.message);
    } finally {
      setUpdatingStatus(null);
      setOpenUserStatusDropdown(null);
    }
  };

  // En gemensam state för modaler
  const [modalContent, setModalContent] = useState(null); // Innehåll att visa i modalen
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Funktion för att öppna modal med rätt innehåll
  const openModal = (type, data = null) => {
    setIsModalOpen(true);
    
    switch(type) {
      case 'ADD_PROJECT':
        setModalContent(
          <AddProject
            builders={builders}
            users={processedUsers}
            setProjects={() => {}} // Not needed with cache
            projects={projects}
            onClose={() => {
              setIsModalOpen(false);
              setModalContent(null);
              refreshProjects(); // Refresh cache instead
            }}
          />
        );
        break;
      case 'UPDATE_PROJECT':
        setModalContent(
          <UpdateProject
            project={data}
            builders={builders}
            users={processedUsers}
            setProjects={() => {}} // Not needed with cache
            projects={projects}
            onClose={() => {
              setIsModalOpen(false);
              setModalContent(null);
              refreshAllData(); // Refresh all caches
            }}
          />
        );
        break;
      case 'ADD_USER':
        setModalContent(
          <AddUser
            users={users}
            setUsers={() => {}} // Not needed with cache
            builders={builders}
            onSuccess={refreshUsers} // Refresh cache after adding user
            onClose={() => {
              setIsModalOpen(false);
              setModalContent(null);
            }}
          />
        );
        break;
      case 'UPDATE_USER':
        setModalContent(
          <UpdateUser
            selectedUser={data}
            users={users}
            setUsers={() => {}} // Not needed with cache
            builders={builders}
            currentUser={currentUser}
            onSuccess={refreshUsers} // Refresh cache after updating user
            onClose={() => {
              setIsModalOpen(false);
              setModalContent(null);
            }}
          />
        );
        break;
      case 'ADD_SUPPLIER':
        setModalContent(
          <AddSupplier
            suppliers={suppliers}
            setSuppliers={() => {}} // Not needed with cache
            users={users}
            onSuccess={refreshSuppliers} // Refresh cache after adding supplier
            onClose={() => {
              setIsModalOpen(false);
              setModalContent(null);
            }}
          />
        );
        break;
      case 'UPDATE_SUPPLIER':
        setModalContent(
          <UpdateSupplier
            supplier={data}
            suppliers={suppliers}
            setSuppliers={() => {}} // Not needed with cache
            users={users}
            onSuccess={refreshSuppliers} // Refresh cache after updating supplier
            onClose={() => {
              setIsModalOpen(false);
              setModalContent(null);
            }}
          />
        );
        break;
      case 'ADD_BUILDER':
        setModalContent(
          <AddBuilder
            builders={builders}
            setBuilders={() => {}} // Not needed with cache
            onSuccess={refreshBuilders} // Refresh cache after adding builder
            onClose={() => {
              setIsModalOpen(false);
              setModalContent(null);
            }}
          />
        );
        break;
      case 'UPDATE_BUILDER':
        setModalContent(
          <UpdateBuilder
            builder={data}
            builders={builders}
            setBuilders={() => {}} // Not needed with cache
            users={users}
            onSuccess={refreshBuilders} // Refresh cache after updating builder
            onClose={() => {
              setIsModalOpen(false);
              setModalContent(null);
            }}
          />
        );
        break;
      default:
        console.warn('Unknown modal type:', type);
        return;
    }
  };



  // Lägg till denna state högst upp med de andra states
  const [builderFilterName, setBuilderFilterName] = useState('');

  // 🚀 OPTIMERING: Memoized byggarfiltrering
  const filteredBuilders = useMemo(() => {
    if (!builders?.length) return [];
    
    return builders.filter(builder => {
      if (!builder) return false;
      
      const searchTerm = builderFilterName.toLowerCase();
      if (!searchTerm) return true;
      
      const companyName = builder.companyName?.toLowerCase() || '';
      
      return companyName.includes(searchTerm);
    });
  }, [builders, builderFilterName]);

  // Lägg till denna state med dina andra states
  const [currentUser, setCurrentUser] = useState(null);

  // 🚀 OPTIMERING: Använd AuthContext istället för separat API-anrop
  const { user: authUser } = useAuth();
  
  useEffect(() => {
    if (authUser) {
      setCurrentUser(authUser);
    }
  }, [authUser]);



  // 🚀 OPTIMERING: Memoized filtering för bättre prestanda (placerad efter getProjectStatus)
  const filteredProjects = useMemo(() => {
    if (!projects?.length) return [];
    
    return projects.filter((project) => {
      if (!project?.customer) return false;
      
      const searchTerm = filterName.toLowerCase();
      // Både föreningen och kontaktpersonen ska ge träff
      const customerName = hamtaSokbartKundnamn(project.customer);
      const projectIdStr = project.projectId ? `#${project.projectId}`.toLowerCase() : '';
      const address = (project.address || '').toLowerCase();
      const city = (project.city || '').toLowerCase();

      const nameMatch = searchTerm === '' ||
        customerName.includes(searchTerm) ||
        projectIdStr.includes(searchTerm) ||
        address.includes(searchTerm) ||
        city.includes(searchTerm);
      
      // Team-sökning
      const teamTerm = filterTeam.toLowerCase();
      const consultantName = project.consultant ? 
        `${project.consultant.firstName || ''} ${project.consultant.lastName || ''}`.toLowerCase() : '';
      const architectName = project.architect ? 
        `${project.architect.firstName || ''} ${project.architect.lastName || ''}`.toLowerCase() : '';
      const builderName = project.builder ? 
        `${project.builder.companyName || ''}`.toLowerCase() : '';
      
      const teamMatch = !teamTerm ||
        consultantName.includes(teamTerm) ||
        architectName.includes(teamTerm) ||
        builderName.includes(teamTerm);
      
      const projectStatus = getProjectStatus(project).status;
      let status = projectStatus;
      
      // Mappa status för filterlogik
      if (projectStatus === 'Planerad start') status = 'Planerad';
      if (projectStatus === 'Avslutad') status = 'Avslutat';
      if (projectStatus === 'Projektering') status = 'Saknas'; // Mappa "Projektering" till "Saknas" filter
      
      const statusMatch = filterStatus[status] !== false;
      
      return nameMatch && teamMatch && statusMatch;
    });
  }, [projects, filterName, filterTeam, filterStatus, getProjectStatus]);

  // 🚀 OPTIMERING: Memoized leverantörsfiltrering
  const filteredSuppliers = useMemo(() => {
    if (!suppliers?.length) return [];
    
    return suppliers.filter(supplier => {
      if (!supplier) return false;
      
      const searchMatch = !supplierFilterName || 
        (supplier.companyName || '').toLowerCase().includes(supplierFilterName.toLowerCase()) ||
        (supplier.contactPerson || '').toLowerCase().includes(supplierFilterName.toLowerCase()) ||
        // Sök även på e-post — för leverantörer utan namngiven kontakt är det
        // enda sättet att hitta dem på kontaktuppgiften.
        (supplier.email || '').toLowerCase().includes(supplierFilterName.toLowerCase()) ||
        (supplier.primaryContactPerson ?
          `${supplier.primaryContactPerson.firstName} ${supplier.primaryContactPerson.lastName}`.toLowerCase().includes(supplierFilterName.toLowerCase()) 
          : false);
      
      return searchMatch;
    });
  }, [suppliers, supplierFilterName]);

  // Rollskydd: admin eller användare med hasAdminAccess har tillgång till Dashboard
  if (user && user.role !== 'admin' && !user.hasAdminAccess) {
    return (
      <div>
        <Navbar currentUser={currentUser} />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-yellow-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h3 className="text-lg font-medium text-yellow-800">Begränsad åtkomst</h3>
                <p className="text-yellow-700 mt-1">
                  Denna sida är endast tillgänglig för admin-användare.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar currentUser={currentUser} />
      <div className="container mx-auto px-4">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {['Projekt', 'Användare', 'Leverantörer', 'Byggare', 'IA-prislista', 'Testkunder', 'Projektörer'].map((tab, index) => (
              <button
                key={tab}
                onClick={() => setTabIndex(index)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm
                  ${tabIndex === index
                    ? 'border-leadme-pink text-leadme-pink'
                    : 'border-transparent text-gray-500 hover:text-leadme-pink hover:border-leadme-pink'}
                `}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="w-full">
          {/* Projekt Tab */}
          {tabIndex === 0 && (
            <div className="space-y-6">
              {/* Rad 1: Skapa projekt */}
              <div className="flex mb-2">
                <button
                  onClick={() => openModal('ADD_PROJECT')}
                  className="inline-flex items-center px-4 py-2 bg-leadme-pink hover:bg-leadme-pink-dark text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-md active:scale-95"
                  aria-label="Skapa projekt"
                  tabIndex={0}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Skapa projekt
                </button>
              </div>
              {/* Rad 2: Filterrad med tre kolumner */}
              <div className="flex flex-wrap gap-4 items-end">
                {/* Sök projekt/kund/adress */}
                <div className="w-full md:w-1/4">
                  <label className="block text-sm font-medium text-leadme-gray mb-1" htmlFor="search-project-customer">Sök projekt, kund, adress</label>
                  <div className="relative">
                    <input
                      id="search-project-customer"
                      type="text"
                      placeholder="Sök projekt id, kund eller adress"
                      value={filterName}
                      onChange={(e) => setFilterName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
                      aria-label="Sök projekt, kund eller adress"
                    />
                    {filterName && (
                      <button
                        onClick={() => setFilterName('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-leadme-gray p-1 rounded-full hover:bg-gray-100"
                        title="Rensa sökfält"
                        aria-label="Rensa sökfält"
                        tabIndex={0}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                {/* Sök byggkonsult/inredare/byggföretag */}
                <div className="w-full md:w-1/4">
                  <label className="block text-sm font-medium text-leadme-gray mb-1" htmlFor="search-team">Sök byggkonsult, inredare, byggföretag</label>
                  <div className="relative">
                    <input
                      id="search-team"
                      type="text"
                      placeholder="Sök byggkonsult, inredare eller byggare"
                      value={filterTeam}
                      onChange={(e) => setFilterTeam(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
                      aria-label="Sök byggkonsult, inredare eller byggare"
                    />
                    {filterTeam && (
                      <button
                        onClick={() => setFilterTeam('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-leadme-gray p-1 rounded-full hover:bg-gray-100"
                        title="Rensa team-sökfält"
                        aria-label="Rensa team-sökfält"
                        tabIndex={0}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                {/* Statusfilter */}
                <div className="w-full md:w-auto">
                  <span className="block text-sm font-medium text-leadme-gray mb-1">Filtrera på status</span>
                  <div className="flex flex-wrap gap-2 items-center">
                    <button
                      onClick={() => handleStatusFilterChange('Planerad')}
                      className={`px-3 py-1 rounded-full text-sm flex items-center gap-1.5 ${
                        filterStatus.Planerad
                          ? 'bg-blue-50 text-blue-800 border border-blue-300'
                          : 'bg-gray-50 text-gray-500 border border-gray-200 hover:border-leadme-pink-light'
                      }`}
                      aria-label="Filtrera på planerade projekt"
                      tabIndex={0}
                    >
                      <span className={`w-3 h-3 rounded-full ${filterStatus.Planerad ? 'bg-blue-500' : 'bg-gray-300'}`}></span>
                      Ej startat
                    </button>
                    <button
                      onClick={() => handleStatusFilterChange('Pågående')}
                      className={`px-3 py-1 rounded-full text-sm flex items-center gap-1.5 ${
                        filterStatus.Pågående
                          ? 'bg-orange-50 text-orange-800 border border-orange-300'
                          : 'bg-gray-50 text-gray-500 border border-gray-200 hover:border-leadme-pink-light'
                      }`}
                      aria-label="Filtrera på pågående projekt"
                      tabIndex={0}
                    >
                      <span className={`w-3 h-3 rounded-full ${filterStatus.Pågående ? 'bg-leadme-orange' : 'bg-gray-300'}`}></span>
                      Pågående
                    </button>
                    <button
                      onClick={() => handleStatusFilterChange('Avslutat')}
                      className={`px-3 py-1 rounded-full text-sm flex items-center gap-1.5 ${
                        filterStatus.Avslutat
                          ? 'bg-green-50 text-green-800 border border-green-300'
                          : 'bg-gray-50 text-gray-500 border border-gray-200 hover:border-leadme-pink-light'
                      }`}
                      aria-label="Filtrera på avslutade projekt"
                      tabIndex={0}
                    >
                      <span className={`w-3 h-3 rounded-full ${filterStatus.Avslutat ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                      Avslutat
                    </button>
                    <button
                      onClick={() => handleStatusFilterChange('Saknas')}
                      className={`px-3 py-1 rounded-full text-sm flex items-center gap-1.5 ${
                        filterStatus.Saknas
                          ? 'bg-violet-50 text-violet-800 border border-violet-300'
                          : 'bg-gray-50 text-gray-500 border border-gray-200 hover:border-leadme-pink-light'
                      }`}
                      aria-label="Filtrera på projekt i projektering"
                      tabIndex={0}
                    >
                      <span className={`w-3 h-3 rounded-full ${filterStatus.Saknas ? 'bg-violet-500' : 'bg-gray-300'}`}></span>
                      Projektering
                    </button>
                    <button
                      onClick={resetFilters}
                      className="ml-2 text-xs text-leadme-gray hover:text-leadme-pink flex items-center gap-1 px-2 py-0.5 rounded hover:bg-gray-100 border border-transparent focus:outline-none focus:ring-2 focus:ring-leadme-pink"
                      title="Återställ alla filter"
                      aria-label="Återställ filter"
                      tabIndex={0}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="hidden sm:inline">Återställ filter</span>
                    </button>
                  </div>
                </div>
              </div>
              {/* Projektlista */}
              <div className="bg-white rounded-xl shadow-lg overflow-x-auto">
                <div className="min-w-[900px]">
                {/* Rubrikrad */}
                <div className="grid grid-cols-[1.0fr_2.0fr_2.5fr_1.5fr_1.5fr_1.5fr_2.0fr_1.0fr_80px] gap-4 px-6 py-3 border-b border-gray-200 bg-gray-50">
                  <div className="text-sm font-medium text-gray-500">Projekt id</div>
                  <div className="text-sm font-medium text-gray-500">Kund</div>
                  <div className="text-sm font-medium text-gray-500">Adress</div>
                  <div className="text-sm font-medium text-gray-500">Typ</div>
                  <div className="text-sm font-medium text-gray-500 text-center">Byggkonsult</div>
                  <div className="text-sm font-medium text-gray-500 text-center">Inredare</div>
                  <div className="text-sm font-medium text-gray-500 text-center">Byggare</div>
                  <div className="text-sm font-medium text-gray-500 text-center">Status</div>
                  <div className="text-sm font-medium text-gray-500 text-center">Ta bort</div>
                </div>

                {/* Projektlista */}
                <div className="divide-y divide-gray-200">
                  {filteredProjects.map((project) => (
                    <div 
                      key={project._id} 
                      className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => openModal('UPDATE_PROJECT', project)}
                    >
                      <div className="grid grid-cols-[1.0fr_2.0fr_2.5fr_1.5fr_1.5fr_1.5fr_2.0fr_1.0fr_80px] gap-4 items-center">
                        {/* Projekt id */}
                        <div>
                          <span className="font-medium text-gray-900">
                            {project.projectId ? `#${project.projectId}` : '-'}
                          </span>
                          {project.projectDescription && (
                            <div className="text-sm text-gray-500 mt-1 italic">
                              {project.projectDescription}
                            </div>
                          )}
                        </div>
                        {/* Kund */}
                        <div className="text-gray-600">
                          {project.customer && (
                            <>
                              {hamtaBestallarnamn(project.customer)}
                              {arBrfKund(project.customer) && (
                                <div className="text-sm text-gray-500">
                                  {`${project.customer.firstName || ''} ${project.customer.lastName || ''}`.trim()}
                                </div>
                              )}
                              {project.additionalCustomers?.length > 0 && (
                                <div className="text-sm text-gray-500">
                                  + {project.additionalCustomers.map(c => `${c.firstName} ${c.lastName}`).join(', ')}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {/* Adress */}
                        <div className="text-gray-600">
                          {project.address}, {project.city}
                        </div>
                        {/* Projekttyp */}
                        <div className="text-gray-600">
                          {project.projectType}
                        </div>
                        {/* Byggkonsult */}
                        <div className="flex items-center justify-center w-full">
                          <div className="flex items-center">
                            <div className="relative" style={{ zIndex: 20 }}>
                              <Avatar entity={project.consultant} size="sm" />
                            </div>
                            
                            {/* Visa ytterligare byggkonsulter direkt som avatarer */}
                            {project.additionalConsultants && project.additionalConsultants.length > 0 && (
                              <div className="flex -ml-3" style={{ position: 'relative' }}>
                                {project.additionalConsultants.map((consultant, index) => (
                                  <div 
                                    key={consultant._id} 
                                    className="relative"
                                    style={{ 
                                      marginLeft: "-8px", 
                                      zIndex: 19 - index,
                                      pointerEvents: 'all'
                                    }}
                                  >
                                    <div 
                                      className="border-2 border-white rounded-full overflow-hidden shadow-sm"
                                      style={{ pointerEvents: 'all' }}
                                    >
                                      <Avatar entity={consultant} size="sm" clickable={false} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Inredare */}
                        <div className="flex items-center justify-center w-full">
                          <div className="flex items-center">
                            <div className="relative" style={{ zIndex: 20 }}>
                              <Avatar entity={project.architect} size="sm" />
                            </div>
                            
                            {/* Visa ytterligare inredare direkt som avatarer */}
                            {project.additionalArchitects && project.additionalArchitects.length > 0 && (
                              <div className="flex -ml-3" style={{ position: 'relative' }}>
                                {project.additionalArchitects.map((architect, index) => (
                                  <div 
                                    key={architect._id} 
                                    className="relative"
                                    style={{ 
                                      marginLeft: "-8px", 
                                      zIndex: 19 - index,
                                      pointerEvents: 'all'
                                    }}
                                  >
                                    <div 
                                      className="border-2 border-white rounded-full overflow-hidden shadow-sm"
                                      style={{ pointerEvents: 'all' }}
                                    >
                                      <Avatar entity={architect} size="sm" clickable={false} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Byggföretag */}
                        <div className="flex items-center justify-center w-full">
                          {project.builder ? (
                            <Avatar entity={project.builder} size="sm" />
                          ) : (
                            <span className="text-gray-500">--</span>
                          )}
                        </div>
                        {(() => {
                          const ps = getProjectStatus(project);
                          const label = ps.subStatus ? `${ps.status} · ${ps.subStatus}` : ps.status;
                          return (
                            <div className={`flex flex-col items-center text-center px-2.5 py-1 rounded-full text-xs font-medium ${ps.color}`}>
                              <span className="text-center">{label}</span>
                              {ps.message && (
                                <span className="text-xs text-center">{ps.message}</span>
                              )}
                            </div>
                          );
                        })()}
                        <div 
                          className="text-gray-600 flex justify-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <RemoveProject
                            project={project}
                            setProjects={() => {}} // Not needed with cache
                            projects={projects}
                            onSuccess={refreshProjects} // Refresh cache after removal
                            buttonClassName="p-2 text-leadme-red hover:text-leadme-red-dark hover:bg-leadme-red-light/20 rounded-full transition-colors duration-200"
                            iconClassName="w-5 h-5"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              </div>
            </div>
          )}

          {/* Användare Tab */}
          {tabIndex === 1 && (
            <div className="space-y-6">
              {/* Sökfält och knappar */}
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="w-full md:w-1/3">
                  <input
                    type="text"
                    placeholder="Sök användare"
                    value={userFilterName}
                    onChange={(e) => setUserFilterName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleCleanDeclinedCustomers}
                    disabled={cleaningDeclinedCustomers}
                    className="inline-flex items-center px-4 py-2 bg-leadme-red hover:bg-leadme-red-dark disabled:bg-leadme-red-light text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-md active:scale-95"
                    title="Rensa alla kunder med status Nej tack"
                  >
                    {cleaningDeclinedCustomers ? (
                      <>
                        <svg className="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Rensar...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Rensa Nej-kunder
                      </>
                    )}
                  </button>
                  <button
                    onClick={refreshUsers}
                    disabled={usersLoading}
                    className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-md active:scale-95"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {usersLoading ? 'Uppdaterar...' : 'Uppdatera'}
                  </button>
                  <button
                    onClick={() => openModal('ADD_USER')}
                    className="inline-flex items-center px-4 py-2 bg-leadme-pink hover:bg-leadme-pink-dark text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-md active:scale-95"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Lägg till användare
                  </button>
                </div>
              </div>

              {/* Toggle-knappar för användartyper */}
              <div className="flex flex-wrap space-x-2 space-y-2 sm:space-y-0">
                <button
                  onClick={() => setUserType('all')}
                  className={`px-4 py-2 rounded-lg ${
                    userType === 'all' 
                      ? 'bg-leadme-pink-light text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-leadme-pink-light hover:text-white focus:text-white'
                  }`}
                >
                  Alla
                </button>
                <button
                  onClick={() => setUserType('byggkonsult')}
                  className={`px-4 py-2 rounded-lg ${
                    userType === 'byggkonsult' 
                      ? 'bg-leadme-pink-light text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-leadme-pink-light hover:text-white focus:text-white'
                  }`}
                >
                  Byggkonsulter
                </button>
                <button
                  onClick={() => setUserType('inredningsarkitekt')}
                  className={`px-4 py-2 rounded-lg ${
                    userType === 'inredningsarkitekt' 
                      ? 'bg-leadme-pink-light text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-leadme-pink-light hover:text-white focus:text-white'
                  }`}
                >
                  Inredningsarkitekter
                </button>
                <button
                  onClick={() => setUserType('byggare')}
                  className={`px-4 py-2 rounded-lg ${
                    userType === 'byggare' 
                      ? 'bg-leadme-pink-light text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-leadme-pink-light hover:text-white focus:text-white'
                  }`}
                >
                  Byggare
                </button>
                <button
                  onClick={() => setUserType('UE')}
                  className={`px-4 py-2 rounded-lg ${
                    userType === 'UE'
                      ? 'bg-leadme-pink-light text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-leadme-pink-light hover:text-white focus:text-white'
                  }`}
                >
                  Underleverantörer
                </button>
                <button
                  onClick={() => setUserType('projektor')}
                  className={`px-4 py-2 rounded-lg ${
                    userType === 'projektor'
                      ? 'bg-leadme-pink-light text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-leadme-pink-light hover:text-white focus:text-white'
                  }`}
                >
                  Projektörer
                </button>
                <button
                  onClick={() => setUserType('materialleverantör')}
                  className={`px-4 py-2 rounded-lg ${
                    userType === 'materialleverantör' 
                      ? 'bg-leadme-pink-light text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-leadme-pink-light hover:text-white focus:text-white'
                  }`}
                >
                  Materialleverantörer
                </button>
                <button
                  onClick={() => setUserType('kund')}
                  className={`px-4 py-2 rounded-lg ${
                    userType === 'kund'
                      ? 'bg-leadme-pink-light text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-leadme-pink-light hover:text-white focus:text-white'
                  }`}
                >
                  Kunder
                </button>
              </div>

              {/* Konto-status-filter */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-500 mr-1">Konto:</span>
                {[
                  { value: 'all', label: 'Alla' },
                  { value: 'active', label: 'Aktiv' },
                  { value: 'inactive', label: 'Inaktiv' },
                  { value: 'test', label: 'Testkund' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setUserAccountStatus(opt.value)}
                    className={`px-3 py-1.5 text-sm rounded-lg ${
                      userAccountStatus === opt.value
                        ? 'bg-leadme-pink text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-leadme-pink-light hover:text-white'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Användarlista */}
              <div className="bg-white rounded-xl shadow-lg overflow-x-auto lg:overflow-visible">
                <div className="min-w-[800px]">
                 {/* Rubrikrad */}
                 <div className={`grid gap-4 px-6 py-3 border-b border-gray-200 bg-gray-50 ${
                   userType === 'kund' || userType === 'all'
                     ? 'grid-cols-[2fr_2fr_2fr_1.5fr_1.0fr_1.3fr_80px]'
                     : 'grid-cols-[2fr_2fr_2fr_1.5fr_1.0fr_80px]'
                 }`}>
                  <div className="text-sm font-medium text-gray-500">Namn</div>
                  <div className="text-sm font-medium text-gray-500">E-post</div>
                  <div className="text-sm font-medium text-gray-500">Telefon</div>
                  <div className="text-sm font-medium text-gray-500">Roll/Företag</div>
                   <div className="text-sm font-medium text-gray-500">Konto</div>
                  {(userType === 'kund' || userType === 'all') && (
                    <div className="text-sm font-medium text-gray-500">Status</div>
                  )}
                  <div className="text-sm font-medium text-gray-500 text-center">Ta bort</div>
                </div>

                {/* Användarrader */}
                <ul className="divide-y divide-gray-200">
                  {processedUsers
                    .filter(user => {
                      if (!user) return false;

                      // Filtrera baserat på userType
                      const typeMatch = userType === 'all' || user.role === userType;

                      // Filtrera baserat på konto-status (active/inactive/testkund)
                      const accountStatus = user.status === 'inactive' ? 'inactive' : 'active';
                      const accountMatch =
                        userAccountStatus === 'all' ? true :
                        userAccountStatus === 'test' ? user.isTestCustomer === true :
                        accountStatus === userAccountStatus;

                      // Filtrera baserat på söktext
                      const searchMatch = !userFilterName ||
                        `${user.firstName} ${user.lastName}`.toLowerCase().includes(userFilterName.toLowerCase()) ||
                        user.email.toLowerCase().includes(userFilterName.toLowerCase());

                      return typeMatch && accountMatch && searchMatch;
                    })
                    .map((user) => (
                    <li 
                      key={user._id} 
                      className="px-6 py-4 hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                      onClick={() => openModal('UPDATE_USER', user)}
                    >
                      <div className={`grid gap-4 items-center ${
                        userType === 'kund' || userType === 'all' 
                          ? 'grid-cols-[2fr_2fr_2fr_1.5fr_1.0fr_1.3fr_80px]' 
                          : 'grid-cols-[2fr_2fr_2fr_1.5fr_1.0fr_80px]'
                      }`}>
                        <div className="text-left flex items-center gap-3">
                          {user.avatar ? (
                            <img
                              src={getAvatarUrl(user.avatar)}
                              alt={`${user.firstName} ${user.lastName}`}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-500 font-medium">
                                {user.firstName.charAt(0)}
                              </span>
                            </div>
                          )}
                          <span className="font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </span>
                        </div>
                        <div className="text-left text-gray-600">{user.email}</div>
                        <div className="text-left text-gray-600">{user.phone}</div>
                        <div>
                          <div className="text-gray-600">{user.role}</div>
                          {user.role === 'byggare' && user.builder && (
                            <div className="text-xs text-gray-500">
                              {user.builder.companyName}
                            </div>
                          )}
                          {user.role === 'UE' && user.companyName && (
                            <div className="text-xs text-gray-500">
                              {user.companyName}
                            </div>
                          )}
                          {user.role === 'materialleverantör' && user.companyName && (
                            <div className="text-xs text-gray-500">
                              {user.companyName}
                            </div>
                          )}
                          {(user.role === 'byggkonsult' || user.role === 'inredningsarkitekt') && (
                            <div className="text-xs text-gray-500">
                              Leadme AB
                            </div>
                          )}
                        </div>
                        
                                                {/* Status kolumn - visar procurement eller projektstatus */}
                          <div className="relative">
                            {(() => {
                              const accountStatus = (user.status || 'active');
                              return (
                                <>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setOpenAccountStatusDropdown(openAccountStatusDropdown === `status_${user._id}` ? null : `status_${user._id}`); }}
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${accountStatus === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}
                                    title="Ändra kontostatus"
                                  >
                                    {accountStatus === 'active' ? 'Aktiv' : 'Inaktiv'}
                                  </button>
                                  {openAccountStatusDropdown === `status_${user._id}` && (
                                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow z-10 text-xs">
                                      {['active','inactive'].map(st => (
                                        <button
                                          key={st}
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            try {
                                              const token = localStorage.getItem('token');
                                              const res = await fetch(`${API_URL}/users/${user._id}`, {
                                                method: 'PUT',
                                                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ status: st })
                                              });
                                              if (!res.ok) throw new Error(`HTTP ${res.status}`);
                                              await refreshUsers();
                                              setOpenAccountStatusDropdown(null);
                                            } catch (err) {
                                              alert('Kunde inte uppdatera kontostatus.');
                                            }
                                          }}
                                          className={`block w-full text-left px-3 py-1 hover:bg-gray-100 ${accountStatus === st ? 'font-medium' : ''}`}
                                        >{st === 'active' ? 'Aktiv' : 'Inaktiv'}</button>
                                      ))}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>

                          {(userType === 'kund' || userType === 'all') && (
                          <div className="text-gray-600 relative" onClick={(e) => e.stopPropagation()}>
                            {user.role === 'kund' ? (
                              <div className="relative">
                                {/* Klickbar status-badge */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenUserStatusDropdown(openUserStatusDropdown === user._id ? null : user._id);
                                  }}
                                  disabled={updatingStatus === user._id}
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-leadme-pink transition-all ${
                                    user.currentStatus === 'Förstudie' ? 'bg-blue-100 text-blue-800' :
                                    user.currentStatus === 'Estimat' ? 'bg-orange-100 text-orange-800' :
                                    user.currentStatus === 'Nej tack' ? 'bg-red-100 text-red-800' :
                                    user.currentStatus === 'Hembesök' ? 'bg-indigo-100 text-indigo-800' :
                                    user.currentStatus === 'Kalkyl' ? 'bg-cyan-100 text-cyan-800' :
                                    user.currentStatus === 'PU' ? 'bg-emerald-100 text-emerald-800' :
                                    user.currentStatus === 'Offert' ? 'bg-purple-100 text-purple-800' :
                                    user.currentStatus === 'Projektering' ? 'bg-violet-100 text-violet-800' :
                                    user.currentStatus === 'Pågående' ? 'bg-leadme-orange-light text-leadme-orange-superdark' :
                                    user.currentStatus === 'Planerad' ? 'bg-blue-100 text-blue-800' :
                                    user.currentStatus === 'Avslutad' ? 'bg-green-100 text-green-800' :
                                    user.currentStatus === 'Saknas' ? 'bg-violet-100 text-violet-800' :
                                    user.currentStatus === 'Ingen status' ? 'bg-gray-100 text-gray-500' :
                                    'bg-gray-100 text-gray-800'
                                  } ${updatingStatus === user._id ? 'opacity-50' : ''}`}
                                  title="Klicka för att ändra status"
                                >
                                  {updatingStatus === user._id ? (
                                    <svg className="animate-spin w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                    </svg>
                                  ) : null}
                                  {user.currentStatus}
                                  {user.hasActiveProject && user.currentStatus !== 'Ingen status' && (
                                    <span className="ml-1 text-xs opacity-75">
                                      {user.currentStatus === 'Projektering' ? '📋' : '🏗️'}
                                    </span>
                                  )}
                                  <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>

                                {/* Dropdown med statusar */}
                                {openUserStatusDropdown === user._id && (
                                  <div className="absolute z-50 mt-1 right-0 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[160px]">
                                    {/* Header */}
                                    <div className="px-3 py-2 border-b border-gray-100">
                                      <p className="text-xs font-medium text-gray-500">
                                        {user.hasActiveProject ? 'Projektstatus' : 'Upphandlingsstatus'}
                                      </p>
                                    </div>
                                    
                                    {/* Status-alternativ */}
                                    <div className="max-h-64 overflow-y-auto">
                                      {(user.hasActiveProject ? projectStatuses : procurementStatuses).map((status) => (
                                        <button
                                          key={status.value}
                                          onClick={() => handleStatusChange(user, status.value)}
                                          className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                                            user.currentStatus === status.value ? 'bg-gray-50' : ''
                                          }`}
                                        >
                                          <span className={`inline-block w-2 h-2 rounded-full ${status.color.split(' ')[0]}`}></span>
                                          <span>{status.label}</span>
                                          {user.currentStatus === status.value && (
                                            <svg className="w-4 h-4 ml-auto text-leadme-pink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                            </svg>
                                          )}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        )}
                        
                        {/* Ta bort-knapp med stopPropagation för att förhindra att modalen öppnas */}
                        <div 
                          className="text-gray-600 flex justify-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <RemoveUser
                            user={user}
                            setUsers={() => {}} // Not needed with cache
                            users={users}
                            onSuccess={refreshUsers} // Refresh cache after removal
                            buttonClassName="p-2 text-leadme-red hover:text-leadme-red-dark hover:bg-leadme-red-light/20 rounded-full transition-colors duration-200"
                            iconClassName="w-5 h-5"
                          />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                </div>
              </div>
            </div>
          )}

          {/* Leverantörer Tab */}
          {tabIndex === 2 && (
            <div className="space-y-6">
              {/* Sökfält och Lägg till-knapp */}
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="w-full md:w-1/3">
                  <input
                    type="text"
                    placeholder="Sök leverantör"
                    value={supplierFilterName}
                    onChange={(e) => setSupplierFilterName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => openModal('ADD_SUPPLIER')}
                  className="inline-flex items-center px-4 py-2 bg-leadme-pink hover:bg-leadme-pink-dark text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-md active:scale-95"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Lägg till leverantör
                </button>
              </div>

              {/* Leverantörslista */}
              <div className="bg-white rounded-xl shadow-lg overflow-x-auto">
                <div className="min-w-[800px]">
                {/* Rubrikrad */}
                <div className="grid grid-cols-[2fr_1.5fr_1.5fr_2fr_1fr_1.5fr_80px] gap-4 px-6 py-3 border-b border-gray-200 bg-gray-50">
                  <div className="text-sm font-medium text-gray-500">Företag</div>
                  <div className="text-sm font-medium text-gray-500">Typ</div>
                  <div className="text-sm font-medium text-gray-500">Kontaktperson</div>
                  <div className="text-sm font-medium text-gray-500">Hemsida</div>
                  <div className="text-sm font-medium text-gray-500">Rabatt</div>
                  <div className="text-sm font-medium text-gray-500">Kontaktmetod</div>
                  <div className="text-sm font-medium text-gray-500 text-center">Ta bort</div>
                </div>

                {/* Leverantörsrader */}
                <ul className="divide-y divide-gray-200">
                  {filteredSuppliers.map((supplier, supplierIndex) => (
                    <li 
                      key={supplier._id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => openModal('UPDATE_SUPPLIER', supplier)}
                    >
                      <div className="grid grid-cols-[2fr_1.5fr_1.5fr_2fr_1fr_1.5fr_80px] gap-4 px-6 py-4 items-center">
                        {/* Företagsnamn med logo */}
                        <div className="flex items-center gap-3">
                          {supplier.avatar ? (
                            <img
                              src={getAvatarUrl(supplier.avatar)}
                              alt={`${supplier.companyName} logo`}
                              className="h-10 w-10 object-contain rounded-full bg-white"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                              <span className="text-gray-500 font-medium">
                                {(supplier.companyName || '').charAt(0)}
                              </span>
                            </div>
                          )}
                          <span className="font-medium text-gray-900">{supplier.companyName || ''}</span>
                        </div>

                        {/* Leverantörstyp */}
                        <div className="text-gray-600">
                          {supplier.supplierType}
                        </div>

                        {/* Kontaktperson */}
                        <div className="text-gray-600">
                          {supplier.primaryContactPerson ? (
                            <div className="flex items-center gap-2">
                              {supplier.primaryContactPerson.avatar ? (
                                <img
                                  src={getAvatarUrl(supplier.primaryContactPerson.avatar)}
                                  alt={`${supplier.primaryContactPerson.firstName} ${supplier.primaryContactPerson.lastName}`}
                                  className="h-6 w-6 object-cover rounded-full"
                                />
                              ) : (
                                <div className="h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center">
                                  <span className="text-gray-500 text-xs font-medium">
                                    {(supplier.primaryContactPerson?.firstName || '').charAt(0)}
                                  </span>
                                </div>
                              )}
                              <span>
                                {supplier.primaryContactPerson.firstName} {supplier.primaryContactPerson.lastName}
                              </span>
                            </div>
                          ) : (
                            // Saknas namn men e-post finns: visa e-posten som
                            // kontakt. För mindre leverantörer är den ofta allt
                            // vi har, och "-" gav intrycket att kontaktuppgift
                            // saknades helt.
                            supplier.contactPerson || supplier.email || "-"
                          )}
                        </div>
                        
                        {/* Hemsida */}
                        <div className="text-gray-600">
                          {supplier.website ? (
                            <a 
                              href={supplier.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-leadme-pink hover:text-leadme-pink-dark"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {supplier.website}
                            </a>
                          ) : '-'}
                        </div>

                        {/* Rabatt */}
                        <div className="text-gray-600">
                          {supplier.discountRate}%
                          {supplier.discountDescription && (
                            <span className="text-xs text-gray-500 ml-1">
                              {supplier.discountDescription}
                            </span>
                          )}
                          {supplier.discounts && supplier.discounts.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              <div className="cursor-pointer group relative">
                                <span className="underline">+ Specifika rabatter</span>
                                {/* Fälls uppåt på de sista raderna.
                                    Listan ligger i en overflow-x-auto-behållare,
                                    som klipper även vertikalt — en popover som
                                    öppnas nedåt från sista raden hamnade utanför
                                    och syntes inte. Högre z-index hjälper inte
                                    mot klippning, bara mot staplingsordning. */}
                                <div className={`absolute left-0 hidden group-hover:block bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-30 w-52 ${
                                  filteredSuppliers.length > 3 && supplierIndex >= filteredSuppliers.length - 2
                                    ? 'bottom-full mb-1'
                                    : 'top-full mt-1'
                                }`}>
                                  <ul className="space-y-1">
                                    {supplier.discounts.map((discount, index) => (
                                      <li key={index} className="text-gray-700">
                                        <span className="font-medium">{discount.rate}%</span> på {discount.category}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Kontaktmetod */}
                        <div className="text-gray-600">
                          {supplier.contactMethod === 'direct' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Direktkontakt
                            </span>
                          )}
                          {supplier.contactMethod === 'leadme' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Via Leadme
                            </span>
                          )}
                          {supplier.contactMethod === 'affiliate' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Affiliate-länk
                            </span>
                          )}
                          {!supplier.contactMethod && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Direktkontakt
                            </span>
                          )}
                        </div>

                        {/* Ta bort-knapp */}
                        <div 
                          className="flex justify-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <RemoveSupplier
                            supplier={supplier}
                            setSuppliers={() => {}} // Not needed with cache
                            suppliers={suppliers}
                            onSuccess={refreshSuppliers} // Refresh cache after removal
                            buttonClassName="p-2 text-leadme-red hover:text-leadme-red-dark hover:bg-leadme-red-light/20 rounded-full transition-colors duration-200"
                            iconClassName="w-5 h-5"
                          />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                </div>
              </div>
            </div>
          )}

          {/* Byggföretag Tab */}
          {tabIndex === 3 && (
            <div className="space-y-6">
              {/* Sökfält och Lägg till-knapp */}
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="w-full md:w-1/3">
                  <input
                    type="text"
                    placeholder="Sök byggare"
                    value={builderFilterName}
                    onChange={(e) => setBuilderFilterName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => openModal('ADD_BUILDER')}
                  className="inline-flex items-center px-4 py-2 bg-leadme-pink hover:bg-leadme-pink-dark text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-md active:scale-95"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Lägg till byggare
                </button>
              </div>

              {/* Byggföretagslista */}
              <div className="bg-white rounded-xl shadow-lg overflow-x-auto">
                <div className="min-w-[800px]">
                {/* Rubrikrad - uppdaterad grid-cols */}
                <div className="grid grid-cols-[2fr_2fr_2fr_2fr_1fr_80px] gap-4 px-6 py-3 border-b border-gray-200 bg-gray-50">
                  <div className="text-sm font-medium text-gray-500">Byggare</div>
                  <div className="text-sm font-medium text-gray-500">Hemsida</div>
                  <div className="text-sm font-medium text-gray-500">Kontaktperson</div>
                  <div className="text-sm font-medium text-gray-500">E-post</div>
                  <div className="text-sm font-medium text-gray-500">Telefon</div>
                  <div className="text-sm font-medium text-gray-500 text-center">Ta bort</div>
                </div>

                {/* Byggföretagsrader - uppdaterad grid-cols för att matcha rubrikraden */}
                <div className="divide-y divide-gray-200">
                  {filteredBuilders.map((builder) => (
                    <div 
                      key={builder._id} 
                      className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => openModal('UPDATE_BUILDER', builder)}
                    >
                      <div className="grid grid-cols-[2fr_2fr_2fr_2fr_1fr_80px] gap-4 items-center">
                        {/* Företagsnamn med logo */}
                        <div className="flex items-center gap-3">
                          {builder.avatar ? (
                            <img
                              src={getAvatarUrl(builder.avatar)}
                              alt={`${builder.companyName} logo`}
                              className="h-10 w-10 object-contain rounded-full bg-white"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                              <span className="text-gray-500 font-medium">
                                {(builder.companyName || '').charAt(0)}
                              </span>
                            </div>
                          )}
                          <span className="font-medium text-gray-900">
                            {builder.companyName || ''}
                          </span>
                        </div>

                        {/* Hemsida */}
                        <div className="text-gray-600">
                          {builder.website ? (
                            <a 
                              href={builder.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-leadme-pink hover:text-leadme-pink-dark"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {builder.website}
                            </a>
                          ) : '-'}
                        </div>

                        {/* Kontaktperson */}
                        <div className="flex flex-col">
                          {builder.contactPerson && (
                            <div className="text-gray-600">
                              {builder.contactPerson.firstName} {builder.contactPerson.lastName}
                              {builder.puPriorityContact?._id === builder.contactPerson._id && (
                                <span className="ml-1 text-xs bg-leadme-pink text-white px-1.5 py-0.5 rounded">PU</span>
                              )}
                            </div>
                          )}
                          {builder.additionalContactPersons?.length > 0 && (
                            <div className="text-sm text-gray-500">
                              + {builder.additionalContactPersons.map(person => 
                                `${person.firstName} ${person.lastName}${builder.puPriorityContact?._id === person._id ? ' (PU)' : ''}`
                              ).join(', ')}
                            </div>
                          )}
                          {builder.puPriorityContact && 
                           builder.puPriorityContact._id !== builder.contactPerson?._id && 
                           !builder.additionalContactPersons?.some(p => p._id === builder.puPriorityContact._id) && (
                            <div className="text-sm text-leadme-pink font-medium">
                              PU: {builder.puPriorityContact.firstName} {builder.puPriorityContact.lastName}
                            </div>
                          )}
                        </div>

                        {/* E-post */}
                        <div className="text-gray-600">
                          {builder.contactPerson?.email || '-'}
                        </div>

                        {/* Telefon */}
                        <div className="text-gray-600">
                          {builder.contactPerson?.phone || '-'}
                        </div>

                        {/* Ta bort-knapp */}
                        <div 
                          className="text-gray-600 flex justify-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <RemoveBuilder
                            builder={builder}
                            setBuilders={() => {}} // Not needed with cache
                            builders={builders}
                            onSuccess={refreshBuilders} // Refresh cache after removal
                            buttonClassName="p-2 text-leadme-red hover:text-leadme-red-dark hover:bg-leadme-red-light/20 rounded-full transition-colors duration-200"
                            iconClassName="w-5 h-5"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              </div>
            </div>
          )}

          {/* Testkunder Tab */}
          {tabIndex === 5 && (
            <div className="space-y-6">
              <TestkundPanel testCustomers={(processedUsers || []).filter(u => u?.isTestCustomer)} />
            </div>
          )}

          {/* Projektörer-katalog Tab (mail-only-kontakter) */}
          {tabIndex === 6 && (
            <div className="space-y-6">
              <ProjektorContactAdmin />
            </div>
          )}

          {/* IA-prislista Tab */}
          {tabIndex === 4 && <PrislistaHantering />}
        </div>

        {/* Bekräftelse-modal med preview för rensning av declined kunder */}
        {showConfirmationModal && cleanPreview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true">
            <div className="bg-white w-full max-w-lg rounded-lg shadow-xl overflow-hidden max-h-[85vh] flex flex-col">
              <div className="px-5 py-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Bekräfta rensning</h3>
              </div>
              <div className="px-5 py-4 overflow-y-auto space-y-4">
                <p className="text-sm text-gray-700">
                  Följande <strong>{cleanPreview.customers.length}</strong> kund(er) och all deras data kommer tas bort permanent:
                </p>

                {/* Kundlista */}
                <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                  {cleanPreview.customers.map((c, i) => (
                    <div key={c._id} className={`flex justify-between text-sm py-1 ${i > 0 ? 'border-t border-gray-200' : ''}`}>
                      <span className="font-medium text-gray-800">{c.name || 'Okänd'}</span>
                      <span className="text-gray-500">{c.email}</span>
                    </div>
                  ))}
                </div>

                {/* Data-sammanfattning */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Data som tas bort:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ['Leads', cleanPreview.summary.leads],
                      ['Upphandlingar', cleanPreview.summary.procurements],
                      ['Estimat', cleanPreview.summary.estimat],
                      ['Kalkyler', cleanPreview.summary.kalkyler],
                      ['PU-trådar', cleanPreview.summary.puThreads],
                      ['Offerter', cleanPreview.summary.offerter],
                      ['Offertkommentarer', cleanPreview.summary.offertThreads],
                      ['Projekt', cleanPreview.summary.projects],
                      ['ÄTA', cleanPreview.summary.atas],
                      ['ÄTA-kommentarer', cleanPreview.summary.ataThreads],
                      ['Tidplaner', cleanPreview.summary.timelines],
                      ['Kanban-tavlor', cleanPreview.summary.kanbans],
                      ['Brief', cleanPreview.summary.briefs],
                      ['Inköpslistor', cleanPreview.summary.shoppingLists],
                      ['Rumsbeskrivningar', cleanPreview.summary.rumsas],
                      ['Möten', cleanPreview.summary.meetings],
                      ['Mötesprotokoll', cleanPreview.summary.meetingProtocols],
                      ['Mötesanteckningar', cleanPreview.summary.meetingNotes],
                      ['Todos', cleanPreview.summary.todos],
                      ['Notifikationer', cleanPreview.summary.notifications],
                      ['Upphandlingsuppdateringar', cleanPreview.summary.procurementUpdates],
                      ['Projektuppdateringar', cleanPreview.summary.projectUpdates],
                      ['Inställningar', cleanPreview.summary.userSettings],
                    ].filter(([, count]) => count > 0).map(([label, count]) => (
                      <div key={label} className="flex justify-between text-sm bg-red-50 rounded px-3 py-1.5">
                        <span className="text-gray-700">{label}</span>
                        <span className="font-medium text-red-700">{count}</span>
                      </div>
                    ))}
                    {Object.values(cleanPreview.summary).every(v => v === 0) && (
                      <p className="col-span-2 text-sm text-gray-500">Ingen relaterad data hittades.</p>
                    )}
                  </div>
                </div>

                <p className="text-sm text-red-600 font-medium">
                  SharePoint-mappar flyttas till "Nej tack". Denna åtgärd kan inte ångras.
                </p>
              </div>
              <div className="px-5 py-4 bg-gray-50 border-t flex justify-end gap-2">
                <button
                  onClick={() => { setShowConfirmationModal(false); setCleanPreview(null); }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg"
                >
                  Avbryt
                </button>
                <button
                  onClick={cleanDeclinedCustomers}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Rensa {cleanPreview.customers.length} kund(er)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Resultat-modal för rensningsresultat */}
        <ResultModal
          isOpen={showResultModal}
          onClose={() => {
            setShowResultModal(false);
            setModalResult(null);
          }}
          title={modalResult?.title}
          message={modalResult?.message}
          details={modalResult?.details}
          type={modalResult?.type}
        />

        <Modal isOpen={isModalOpen} onClose={() => {
          setIsModalOpen(false);
          setModalContent(null);
        }}>
          {modalContent}
        </Modal>
      </div>
    </div>
  );
}

export default Dashboard;
